/**
 * "Balance as of today" — the Humaans-style real-time leave balance.
 *
 * Although the portal grants the year's leave upfront, days are *earned*
 * evenly over the accrual window. For most employees the window is the whole
 * calendar year; for a mid-year hire or a dated policy assignment (TASK-348
 * `effective_from`) it starts on that later day — nothing is earned before it,
 * and the (already prorated) allowance is spread over [window start, Dec 31]
 * so the accrued figure reaches the full allowance exactly on Dec 31.
 *
 * The balance is the allowance earned as of a given day, plus carryover (fully
 * earned the previous year), minus days actually TAKEN so far. Booked-ahead
 * leave is NOT subtracted — it hasn't been taken yet.
 *
 * Shared by the server dashboard resolver and the client profile stats card so
 * both surfaces show identical numbers (lives in `common` — the only place
 * both can import runtime code from). The components keep 2 decimals to make
 * the breakdown tooltip readable; the headline balance rounds to 0.1 like
 * Humaans.
 */
const round2 = (n: number): number => Math.round(n * 100) / 100
/** Round to the ledger's 0.1-day granularity (Humaans-style headline rounding). */
export const roundTenth = (n: number): number => Math.round(n * 10) / 10

const DAY_MS = 86_400_000

export interface AccruedBalance {
    /** Current-year norm prorated to `today` (2dp) */
    accruedToDate: number
    /** accruedToDate + carryover + adjustments (2dp) */
    totalAccrued: number
    /** accruedToDate + carryover + adjustments − usedPast (1dp); may be negative */
    balanceAsOfToday: number
}

/**
 * First day of the current year's accrual window: `accrualStart` when it falls
 * inside `today`'s year or later, Jan 1 otherwise (an earlier start means the
 * whole year accrues). Also drives the "Accrued since …" tooltip label.
 */
export const accrualWindowStart = (today: string, accrualStart: string | null = null): string => {
    const jan1 = `${today.slice(0, 4)}-01-01`
    return accrualStart && accrualStart > jan1 ? accrualStart : jan1
}

/**
 * @param allowance current-year entitlement norm (excludes carryover)
 * @param carryover days carried in from last year (fully earned)
 * @param usedPast days actually taken so far this period (excludes booked-ahead)
 * @param today YYYY-MM-DD
 * @param accrualStart YYYY-MM-DD the accrual clock starts (hire date /
 *   assignment `effective_from`); null accrues from Jan 1 as before
 * @param adjustments net dated admin corrections (TASK-567) — facts as of
 *   their date, so added in full like carryover, never prorated
 */
export const computeAccruedBalance = (
    allowance: number,
    carryover: number,
    usedPast: number,
    today: string,
    accrualStart: string | null = null,
    adjustments = 0,
): AccruedBalance => {
    const windowStart = accrualWindowStart(today, accrualStart)
    const yearEnd = `${today.slice(0, 4)}-12-31`

    let accruedRaw = 0
    if (today >= windowStart) {
        const windowDays = Math.floor((Date.parse(yearEnd) - Date.parse(windowStart)) / DAY_MS) + 1
        const elapsedDays = Math.floor((Date.parse(today) - Date.parse(windowStart)) / DAY_MS) + 1
        accruedRaw = allowance * (elapsedDays / windowDays)
    }

    return {
        accruedToDate: round2(accruedRaw),
        totalAccrued: round2(accruedRaw + carryover + adjustments),
        balanceAsOfToday: roundTenth(accruedRaw + carryover + adjustments - usedPast),
    }
}
